import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { MEASUREMENT_CALCULATION_TYPE_PERCENTAGE } from '../../../constants/measurement-calculation-types';
import SharedWithFDADisclaimer from '../../shared/components/SharedWithFDADisclaimer';
import { getXAxisLabel, getYAxisLabel } from './GraphLabels';
import { itemSorterFn } from '../../../utils/chartUtils';

const LEGEND_ORDER = {
  UpperLimit: 1,
  LowerLimit: 2,
  Actual: 3
};

const getDataPoints = performanceItem => {
  return performanceItem.DataPoints;
};

/**
 * @typedef {object} ActualPerformanceGraphProps
 * @property {Object} [performanceItem]
 * @property {(date: string, performanceItem: { DataPoints: Array<{ Date: string }>, MeasurementName: string }) => void} [dateClick]
 * @property {(performanceItem: { MeasurementName: string }) => void} [titleClick]
 * @property {boolean} [sharedWithFda]
 */

/**
 * @param {ActualPerformanceGraphProps} props
 */
const ActualPerformanceGraph = props => {
  const {
    dateClick,
    titleClick,
    performanceItem = {
      DataPoints: []
    },
    sharedWithFda = false
  } = props;

  const data = getDataPoints(performanceItem);
  const sortedData = data.sort((a, b) => Number(new Date(a.Date)) - Number(new Date(b.Date)));

  const formatter = value => {
    if (performanceItem.MeasurementCalculationId == MEASUREMENT_CALCULATION_TYPE_PERCENTAGE) {
      return `${value}%`;
    }
    return value;
  };

  return (
    <div className='actual-report__graph' onClick={e => e.stopPropagation()}>
      <div className='actual-report__graph-name'>
        <span onClick={() => titleClick(performanceItem)}>{performanceItem.MeasurementName}</span>
      </div>

      {sharedWithFda && (
        <div className='shared-with-fda-graph-sub-title'>
          <SharedWithFDADisclaimer />
        </div>
      )}
      {performanceItem.IsDiscontinued && (
        <div className='warning-message'>
          <span> This metric is expiring on {performanceItem.ExpirationDate}</span>
        </div>
      )}
      <ResponsiveContainer width='100%'>
        <LineChart
          layout='horizontal'
          data={sortedData}
          height={300}
          margin={{
            top: 30,
            right: 20,
            left: 20,
            bottom: 5
          }}>
          <CartesianGrid strokeDasharray='3 3' />
          <YAxis tickFormatter={formatter}>{getYAxisLabel(performanceItem.Measurement)}</YAxis>
          <XAxis
            dataKey='Date'
            type='category'
            onClick={element => dateClick(element.value, performanceItem)}>
            {getXAxisLabel('Data Collection Date')}
          </XAxis>
          <Tooltip formatter={formatter} itemSorter={itemSorterFn(LEGEND_ORDER)} />
          <Legend
            wrapperStyle={{ paddingTop: '1.5rem' }}
            align='center'
            itemSorter={itemSorterFn(LEGEND_ORDER)}
          />
          <Line
            name='Upper Limit'
            dataKey='UpperLimit'
            stroke='#E31837'
            strokeWidth={2}
            legendType={
              performanceItem.DataPoints.filter(d => d.UpperLimit != null).length > 0
                ? 'line'
                : 'none'
            }
          />
          <Line
            name='Lower Limit'
            dataKey='LowerLimit'
            stroke='#E31837'
            strokeWidth={2}
            legendType={
              performanceItem.DataPoints.filter(d => d.LowerLimit != null).length > 0
                ? 'line'
                : 'none'
            }
          />
          <Line dataKey='Actual' stroke='#0F69A0' strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ActualPerformanceGraph;
